import INVENTORY_API_BASE_URL from "../../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import "./ReturnSales.css";

function ReturnSales() {
  const [returnData, setReturnData] = useState([]);
  const [purchaseSummary, setPurchaseSummary] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [skuFilter, setSkuFilter] = useState("");

  const [distributorFilter, setDistributorFilter] = useState("");

  const distributors = [...new Set(returnData.map((item) => item.Distributor))];

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const documentsPerPage = 20;

  useEffect(() => {
    axios
      .get(`${INVENTORY_API_BASE_URL}/getManagereturn`)
      .then((res) => {
        const reversed = (res.data || []).reverse();
        setReturnData(reversed);
      })
      .catch((err) => console.error("Return API Error:", err));

    axios
      .get(`${INVENTORY_API_BASE_URL}/api/return/purchase-summary`)
      .then((res) => {
        setPurchaseSummary(res.data || []);
      })
      .catch((err) => console.error("Purchase Summary API Error:", err));
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [skuFilter]);

  const calculateSalesPrice = (mrp, discount) => {
    return ((100 - discount) / 100) * mrp;
  };

  const getPrintingValue = (grnNo, sku) => {
    const summary = purchaseSummary.find(
      (item) => item.grnNo === grnNo && item.sku === sku
    );
    return summary ? summary.totalValue.toFixed(2) : "-";
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [skuFilter, distributorFilter]);

  const filteredData = returnData
    .filter(
      (record) =>
        distributorFilter === "" || record.Distributor === distributorFilter
    )
    .filter((record) => {
      if (!startDate && !endDate) return true;
      const recordDate = new Date(record.Date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      if (start && end) return recordDate >= start && recordDate <= end;
      if (start) return recordDate >= start;
      if (end) return recordDate <= end;
      return true;
    })
    .map((record) => {
      const matchingEntries = record.Entries.filter((entry) =>
        entry.SKU.toUpperCase().includes(skuFilter.trim().toUpperCase())
      );
      return matchingEntries.length > 0
        ? { ...record, Entries: matchingEntries }
        : null;
    })
    .filter(Boolean);
  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate]);

  const indexOfLastDoc = currentPage * documentsPerPage;
  const indexOfFirstDoc = indexOfLastDoc - documentsPerPage;
  const currentDocuments = filteredData.slice(indexOfFirstDoc, indexOfLastDoc);
  const totalPages = Math.ceil(filteredData.length / documentsPerPage);

  const exportToCSV = () => {
    if (filteredData.length === 0) {
      alert("No data to export!");
      return;
    }

    let csvRows = [];

    csvRows.push(
      [
        "Distributor",
        "Date",
        "Grn No",
        "SKU",
        "Title",
        "Medium",
        "Edition",
        "MRP",
        "Return_Quantity",
        "Sales Price",
        "Sales Value",
        "Printing Value",
      ].join(",")
    );

    filteredData.forEach((record) => {
      record.Entries.forEach((item) => {
        const salesPrice = calculateSalesPrice(item.mrp, item.discount).toFixed(
          2
        );
        const printingValue = getPrintingValue(record.OrderSeq, item.SKU);
        const row = [
          record.Distributor,
          record.Date,
          record.OrderSeq,
          item.SKU,
          item.Title,
          item.Medium,
          item.Edition,
          item.mrp,
          item.Return_Quantity,
          salesPrice,
          (salesPrice * item.Return_Quantity).toFixed(2),
          printingValue,
        ];
        csvRows.push(row.join(","));
      });
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ReturnSales_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  return (
    <div className="returnsku-container">
      <main className="returnsales-content">
        <header className="returnsales-hero">
          <div className="returnsales-hero-copy">
            <span className="returnsales-eyebrow">RETURNS / SALES</span>
            <h1>Sales return records</h1>
            <p>Review distributor returns, returned quantities, sales values, and printing costs.</p>
          </div>
          <div className="returnsales-hero-summary">
            <span className="returnsales-summary-icon" aria-hidden="true">↩</span>
            <span>
              <strong>{filteredData.length.toLocaleString()} return records</strong>
              <small>Matching the active filters</small>
            </span>
          </div>
        </header>

        <section className="returnsales-metrics" aria-label="Sales return totals">
          <article className="returnsales-metric">
            <span>Records in view</span>
            <strong>{filteredData.length.toLocaleString()}</strong>
            <small>Distributor return documents</small>
          </article>
          <article className="returnsales-metric returnsales-metric--blue">
            <span>Returned units</span>
            <strong>{filteredData.reduce((sum, record) => sum + Number(record.totalquantity || 0), 0).toLocaleString()}</strong>
            <small>Across the filtered records</small>
          </article>
          <article className="returnsales-metric returnsales-metric--green">
            <span>Distributors</span>
            <strong>{new Set(filteredData.map((record) => record.Distributor)).size.toLocaleString()}</strong>
            <small>Represented in this view</small>
          </article>
          <article className="returnsales-metric returnsales-metric--violet">
            <span>SKU lines</span>
            <strong>{filteredData.reduce((sum, record) => sum + record.Entries.length, 0).toLocaleString()}</strong>
            <small>Items in the filtered documents</small>
          </article>
        </section>

        <section className="returnsales-filter-card" aria-labelledby="returnsales-filter-title">
          <div className="returnsales-section-heading">
            <div>
              <span className="returnsales-section-kicker">FIND A RETURN</span>
              <h2 id="returnsales-filter-title">Filters and export</h2>
            </div>
            <button
              type="button"
              className="returnsales-clear-button"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
            >
              Clear dates
            </button>
          </div>

          <div className="returnsales-filter-grid">
            <label className="returnsales-filter-field returnsales-filter-field--wide" htmlFor="returnsales-distributor">
              <span>Distributor</span>
              <select
                id="returnsales-distributor"
                value={distributorFilter}
                onChange={(e) => setDistributorFilter(e.target.value)}
              >
                <option value="">All distributors</option>
                {distributors.map((d, idx) => <option key={idx} value={d}>{d}</option>)}
              </select>
            </label>
            <label className="returnsales-filter-field returnsales-filter-field--search" htmlFor="returnsales-sku">
              <span>Search SKU</span>
              <input
                id="returnsales-sku"
                type="text"
                value={skuFilter}
                onChange={(e) => setSkuFilter(e.target.value)}
                placeholder="Enter a SKU"
              />
            </label>
            <label className="returnsales-filter-field" htmlFor="returnsales-start">
              <span>Return date from</span>
              <input
                id="returnsales-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </label>
            <label className="returnsales-filter-field" htmlFor="returnsales-end">
              <span>Return date to</span>
              <input
                id="returnsales-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </label>
            <button type="button" className="returnsales-export-button" onClick={exportToCSV}>
              <span aria-hidden="true">↓</span> Download CSV
            </button>
          </div>
        </section>

        <section className="returnsales-records" aria-labelledby="returnsales-records-title">
          <div className="returnsales-section-heading returnsales-records-heading">
            <div>
              <span className="returnsales-section-kicker">RETURN REGISTER</span>
              <h2 id="returnsales-records-title">Return documents</h2>
              <p>Showing up to {documentsPerPage} documents on each page.</p>
            </div>
            <span className="returnsales-record-count">{filteredData.length.toLocaleString()} documents</span>
          </div>

          {currentDocuments.length > 0 ? (
            <div className="returnsales-document-list">
              {currentDocuments.map((record, index) => (
                <article className="returnsales-document-card" key={index}>
                  <header className="returnsales-document-heading">
                    <div className="returnsales-document-title">
                      <span className="returnsales-document-kicker">DISTRIBUTOR RETURN</span>
                      <h3>{record.Distributor}</h3>
                      <p>{record.Date} <span aria-hidden="true">·</span> GRN {record.OrderSeq}</p>
                    </div>
                    <div className="returnsales-document-summary">
                      <div><span>Return quantity</span><strong>{record.totalquantity}</strong></div>
                      <div><span>Amount value</span><strong>₹{record.totalAmountValue}</strong></div>
                      <div>
                        <span>Printing value</span>
                        <strong>₹{record.Entries.reduce((sum, item) => {
                          const value = getPrintingValue(record.OrderSeq, item.SKU);
                          return sum + (isNaN(parseFloat(value)) ? 0 : parseFloat(value));
                        }, 0).toFixed(2)}</strong>
                      </div>
                    </div>
                  </header>

                  <div className="returnsales-table-scroll">
                    <table className="returnsales-table">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Title</th>
                          <th>Medium</th>
                          <th>Edition</th>
                          <th>MRP</th>
                          <th>Return quantity</th>
                          <th>Sales price</th>
                          <th>Sales value</th>
                          <th>Printing value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {record.Entries.map((item, idx) => {
                          const salesPrice = calculateSalesPrice(item.mrp, item.discount).toFixed(2);
                          const printingValue = getPrintingValue(record.OrderSeq, item.SKU);
                          return (
                            <tr key={idx}>
                              <td className="returnsales-sku-cell">{item.SKU}</td>
                              <td className="returnsales-title-cell">{item.Title}</td>
                              <td>{item.Medium}</td>
                              <td>{item.Edition}</td>
                              <td>{item.mrp}</td>
                              <td>{item.Return_Quantity}</td>
                              <td>{salesPrice}</td>
                              <td>{(salesPrice * item.Return_Quantity).toFixed(2)}</td>
                              <td>{printingValue}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="returnsales-empty-state">
              <strong>No return documents found</strong>
              <span>Change the distributor, SKU, or date range to see other records.</span>
            </div>
          )}

          <nav className="returnsales-pagination" aria-label="Return document pages">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong></span>
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </nav>
        </section>
      </main>
    </div>
  );
}

export default ReturnSales;
